import type { CategoryData, CategoryFormState } from '@openx/types/targeting/category.ts';
import type { TargetingProps } from '@openx/types/targeting/targeting.ts';
import { noop } from '@openx/utils/noop.ts';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useMemo, useState } from 'react';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer.tsx';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer.tsx';
import { CategoryItems } from './CategoryItems.tsx';
import { CategoryReadOnly } from './CategoryReadOnly.tsx';
import { CategoryTopBox } from './CategoryTopBox.tsx';
import { useResetCategoryToAccountDefault } from './hooks/useResetCategoryToAccountDefault.tsx';
import { getDefaultParams, mapCategoryToApi, mapCategoryToFormState } from './utils.ts';

const defaultState = getDefaultParams();

export const Category = memo(function Category({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
  accountUid,
}: TargetingProps<CategoryData>) {
  const initState = useMemo(() => mapCategoryToFormState(cloneDeep(targetingParams)), [targetingParams]);
  const [params, updateParams] = useState<CategoryFormState>(initState);

  const isDataUpdated = useMemo(
    () => !isEqual(targetingParams, params.val.size ? mapCategoryToApi(params) : null),
    [params, targetingParams]
  );

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => onFieldUpdate(field, params.val.size ? mapCategoryToApi(params) : null),
    restoreParams: () => updateParams({ ...initState }),
  });

  const onRemoveAll = useCallback(() => {
    onFieldUpdate(field, null);
    updateParams(defaultState);
  }, [onFieldUpdate, field]);

  const onCategoryChange = useCallback(
    (categories: Set<string>) => {
      updateParams({ op: params.op, val: categories });
    },
    [params.op]
  );

  const { handleResetToAccountDefault, inheritanceInfoComponent, shouldDisplayResetButton } =
    useResetCategoryToAccountDefault({
      accountUid,
      field,
      initState,
      onFieldUpdate,
      onRemoveAll,
      updateParams,
    });

  return (
    <>
      <CategoryReadOnly
        name={name}
        field={field}
        readonly={readonly}
        targetingParams={targetingParams}
        onOpenDrawerClick={onDrawerOpen}
        onRemoveClick={onRemoveAll}
      >
        {inheritanceInfoComponent}
      </CategoryReadOnly>

      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isDataUpdated}>
          <CategoryTopBox
            items={params.val}
            onChange={onCategoryChange}
            handleReset={handleResetToAccountDefault}
            shouldDisplayResetButton={shouldDisplayResetButton}
          />

          <CategoryItems targetingParams={params} readonly={false} onChange={updateParams} />
        </TargetingDrawer>
      )}
    </>
  );
});
